/**
 * MOCK DATA — clearly-labeled synthetic fixtures for local development only.
 * Served when VITE_MOCK=1. Every value below is invented for UI work and is
 * rendered under a persistent "MOCK DATA" banner. Nothing here represents
 * real market data, real prices, or real on-chain state.
 *
 * Shapes mirror the gateway's ACTUAL raw payloads (src/api/types.ts).
 * Symbols use a MOCK prefix so the synthetic origin is obvious even in
 * screenshots.
 */

import type {
  AdminHealthResponse,
  ApproveResponse,
  AuditResponse,
  ConfigListResponse,
  ConfigPutResponse,
  FilterResult,
  HealthResponse,
  KillSwitchResponse,
  MomentumRow,
  PauseResumeResponse,
  PerformancePoint,
  PerformanceResponse,
  PortfolioCurrentResponse,
  PortfolioHistoryResponse,
  PostsResponse,
  RealBrokerResponse,
  RebalanceResponse,
  Trade,
  TradeDetailResponse,
  TradesResponse,
  UniverseCandidate,
  UniverseResponse,
} from './types'

const NOW = new Date('2026-07-22T12:00:00Z').getTime()
const DAY = 24 * 3600 * 1000
const daysAgo = (n: number) => NOW - n * DAY

/** Deterministic pseudo-random (mulberry32) so fixtures are stable. */
function rng(seed: number) {
  let a = seed >>> 0
  return () => {
    a |= 0
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

const MOCK_TOKENS = [
  { symbol: 'MCKA', name: 'MOCK Alpha Corp • Robinhood Token' },
  { symbol: 'MCKB', name: 'MOCK Beta Industries • Robinhood Token' },
  { symbol: 'MCKC', name: 'MOCK Gamma Systems • Robinhood Token' },
  { symbol: 'MCKD', name: 'MOCK Delta Labs • Robinhood Token' },
  { symbol: 'MCKE', name: 'MOCK Epsilon Energy • Robinhood Token' },
  { symbol: 'MCKF', name: 'MOCK Zeta Freight • Robinhood Token' },
] as const

const mockAddr = (i: number) =>
  `0x${(i + 1).toString(16).padStart(4, '0')}${'ab'.repeat(18)}`
    .slice(0, 42)
    .toLowerCase()

// ---------------------------------------------------------------------------
// Public fixtures
// ---------------------------------------------------------------------------

export function mockHealth(): HealthResponse {
  return {
    ok: true,
    service: 'stonker-gateway',
    environment: 'mock',
    db: true,
    ts: NOW,
  }
}

/** live weights keyed by mock token address (cash implicit ~15%). */
const MOCK_WEIGHTS: Record<string, number> = {
  [mockAddr(0)]: 0.24,
  [mockAddr(1)]: 0.21,
  [mockAddr(2)]: 0.17,
  [mockAddr(3)]: 0.13,
  [mockAddr(4)]: 0.1,
}

const MOCK_NAV = 10412.87

export function mockPortfolioCurrent(): PortfolioCurrentResponse {
  const rand = rng(7)
  const positions: Record<string, unknown> = { cashUsd: 1562.11 }
  for (const [addr, w] of Object.entries(MOCK_WEIGHTS)) {
    const priceUsd = Number((20 + rand() * 300).toFixed(2))
    positions[addr] = {
      qty: Number(((MOCK_NAV * w) / priceUsd).toFixed(4)),
      priceUsd,
    }
  }
  return {
    state: {
      id: 'mock-state-015',
      asOf: NOW - 22 * 60 * 1000,
      status: 'live',
      weights: { ...MOCK_WEIGHTS },
      positions,
      navUsd: MOCK_NAV,
      optimizerMeta: { method: 'slsqp', solveMs: 412, note: 'MOCK diagnostics' },
      workflowId: 'mock-wf-20260722-a',
      prevStateId: 'mock-state-014',
      aiSummary:
        'MOCK: rebalanced into 5 positions; momentum broadening, cash buffer 15%.',
      updatedAt: NOW - 22 * 60 * 1000,
    },
  }
}

export function mockPortfolioHistory(): PortfolioHistoryResponse {
  const rand = rng(11)
  const items = []
  for (let d = 14; d >= 0; d--) {
    const raw = MOCK_TOKENS.slice(0, 5).map(() => 0.5 + rand())
    const cash = 0.6 + rand() * 0.6
    const total = raw.reduce((a, b) => a + b, 0) + cash
    const weights: Record<string, number> = {}
    MOCK_TOKENS.slice(0, 5).forEach((_, i) => {
      weights[mockAddr(i)] = Number(((raw[i] ?? 0) / total).toFixed(4))
    })
    items.push({
      id: `mock-state-${String(15 - d).padStart(3, '0')}`,
      asOf: daysAgo(d),
      status: d === 0 ? ('live' as const) : ('superseded' as const),
      navUsd: Number((10000 * (1 + (14 - d) * 0.003 + rand() * 0.01)).toFixed(2)),
      weights,
    })
  }
  return { items }
}

export function mockPerformance(): PerformanceResponse {
  // Same math as the gateway's computePerformance, over the mock NAV path.
  const navs = mockPortfolioHistory().items.filter(
    (i) => typeof i.navUsd === 'number',
  )
  if (navs.length === 0) return { performance: null }
  const first = navs[0]!
  let peak = first.navUsd as number
  let maxDrawdown = 0
  let best: number | null = null
  let worst: number | null = null
  const points: PerformancePoint[] = navs.map((p, i) => {
    const nav = p.navUsd as number
    peak = Math.max(peak, nav)
    const drawdown = nav / peak - 1
    maxDrawdown = Math.min(maxDrawdown, drawdown)
    if (i > 0) {
      const r = nav / (navs[i - 1]!.navUsd as number) - 1
      best = best === null ? r : Math.max(best, r)
      worst = worst === null ? r : Math.min(worst, r)
    }
    return {
      t: p.asOf,
      nav,
      cumReturn: nav / (first.navUsd as number) - 1,
      drawdown,
    }
  })
  const last = navs[navs.length - 1]!
  return {
    performance: {
      start: first.asOf,
      end: last.asOf,
      navStart: first.navUsd as number,
      navEnd: last.navUsd as number,
      totalReturn: (last.navUsd as number) / (first.navUsd as number) - 1,
      maxDrawdown,
      bestDayReturn: best,
      worstDayReturn: worst,
      points,
    },
  }
}

const MOCK_TRADES: Trade[] = [
  {
    id: 'mock-t-004',
    workflowId: 'mock-wf-20260722-a',
    portfolioStateId: 'mock-state-015',
    seq: 1,
    side: 'buy',
    tokenAddress: mockAddr(2),
    symbol: 'MCKC',
    qty: '3.42',
    estPriceUsd: 141.02,
    maxSlippageBps: 50,
    router: '0x-rfq',
    status: 'confirmed',
    reason: null,
    createdAt: NOW - 40 * 60 * 1000,
    updatedAt: NOW - 38 * 60 * 1000,
  },
  {
    id: 'mock-t-003',
    workflowId: 'mock-wf-20260721-a',
    portfolioStateId: 'mock-state-014',
    seq: 2,
    side: 'sell',
    tokenAddress: mockAddr(0),
    symbol: 'MCKA',
    qty: '1.1',
    estPriceUsd: 305.44,
    maxSlippageBps: 50,
    router: 'uniswap',
    status: 'confirmed',
    reason: null,
    createdAt: daysAgo(1),
    updatedAt: daysAgo(1) + 90 * 1000,
  },
  {
    id: 'mock-t-002',
    workflowId: 'mock-wf-20260720-a',
    portfolioStateId: 'mock-state-013',
    seq: 1,
    side: 'buy',
    tokenAddress: mockAddr(1),
    symbol: 'MCKB',
    qty: '12.0',
    estPriceUsd: 24.31,
    maxSlippageBps: 50,
    router: '1inch',
    status: 'failed',
    reason: 'MOCK: quote slippage exceeded maxSlippageBps, aborted',
    createdAt: daysAgo(2),
    updatedAt: daysAgo(2) + 45 * 1000,
  },
  {
    id: 'mock-t-001',
    workflowId: 'mock-wf-20260719-a',
    portfolioStateId: 'mock-state-012',
    seq: 1,
    side: 'buy',
    tokenAddress: mockAddr(3),
    symbol: 'MCKD',
    qty: '5.5',
    estPriceUsd: 88.9,
    maxSlippageBps: 50,
    router: '0x-rfq',
    status: 'submitted',
    reason: null,
    createdAt: daysAgo(3),
    updatedAt: daysAgo(3) + 30 * 1000,
  },
]

export function mockTrades(): TradesResponse {
  return { items: MOCK_TRADES, nextCursor: null }
}

export function mockTradeDetail(id: unknown): TradeDetailResponse {
  const trade =
    MOCK_TRADES.find((t) => t.id === id) ?? MOCK_TRADES[0]!
  const receipts =
    trade.status === 'confirmed' || trade.status === 'submitted'
      ? [
          {
            id: `${trade.id}-r1`,
            txHash: `0x${'3c'.repeat(32)}`,
            status:
              trade.status === 'confirmed'
                ? ('paper' as const)
                : ('pending' as const),
            blockNumber: trade.status === 'confirmed' ? 184_221 : null,
            gasUsed: trade.status === 'confirmed' ? '184000' : null,
            effectiveGasPrice: trade.status === 'confirmed' ? '12000000' : null,
            fillPriceUsd: trade.estPriceUsd,
            fillQty: trade.qty,
            confirmations: trade.status === 'confirmed' ? 3 : 0,
            updatedAt: trade.updatedAt,
          },
        ]
      : []
  return { trade: { ...trade, receipts } }
}

export function mockUniverse(): UniverseResponse {
  const candidates: UniverseCandidate[] = MOCK_TOKENS.map((t, i) => ({
    address: mockAddr(i),
    name: t.name,
    symbol: t.symbol,
    decimals: 18,
    iconUrl: null,
    holders: 400 + i * 37,
    vol24hUsd: 2_400_000 - i * 350_000,
    source: 'mock-blockscout',
  }))
  return {
    snapshot: {
      id: 'mock-univ-009',
      asOf: NOW - 60 * 60 * 1000,
      source: 'mock-blockscout',
      candidates,
      passed: [mockAddr(0), mockAddr(1), mockAddr(2), mockAddr(3)],
    },
  }
}

export function mockSignals() {
  const rand = rng(23)
  const filters: FilterResult[] = MOCK_TOKENS.map((_, i) => {
    const failVolume = i === 5 // stage 2
    const failHistory = i === 4 // stage 1
    const pass = !failVolume && !failHistory
    const result: FilterResult = {
      address: mockAddr(i),
      pass,
      reasons: failHistory
        ? ['MOCK: fewer than 10 trading days available']
        : failVolume
          ? ['MOCK: median 7-day volume below $1M floor']
          : i === 3
            ? ['MOCK: admitted on volume only — liquidity unverified']
            : [],
      flags: { liquidity_unverified: i === 3 },
      metrics: {
        tradingDayCount: failHistory ? 4 : 15,
        coverage: failHistory ? 0.61 : 0.94,
        volMed7: failVolume ? 620_000 : 1_900_000 - i * 120_000,
        lastTradeAgeHours: 2 + i,
        zeroVolFrac: 0.05 * i,
      },
    }
    if (!pass) result.stage_failed = failHistory ? 1 : 2
    return result
  })
  const momentum: MomentumRow[] = MOCK_TOKENS.slice(0, 4).map((_, i) => {
    const gate = rand() > 0.3
    return {
      address: mockAddr(i),
      gate,
      S: gate ? Number((rand() * 2.4 - 0.8).toFixed(3)) : null,
      raw: Number((rand() * 0.2 - 0.05).toFixed(4)),
      riskAdj: Number((rand() * 1.4 - 0.4).toFixed(4)),
      volConfirm: Number((0.6 + rand() * 0.8).toFixed(3)),
      rsi: Number((35 + rand() * 40).toFixed(1)),
      positiveMomentum: gate && rand() > 0.4,
      n: 15,
      volMed7: 1_900_000 - i * 120_000,
      liquidityUsd: 0,
    }
  })
  return {
    signals: {
      id: 'mock-sig-009',
      asOf: NOW - 55 * 60 * 1000,
      universeSnapshotId: 'mock-univ-009',
      filters,
      momentum,
      meta: {
        chainAgeDays: 21,
        candlesRefreshed: 6,
        birdeyeUnconfigured: false,
      },
    },
  }
}

export function mockPosts(): PostsResponse {
  return {
    items: [
      {
        id: 'mock-x-002',
        refType: 'rebalance',
        refId: 'mock-wf-20260722-a',
        content:
          'MOCK POST: Rebalanced. 5 positions, 15% cash buffer. NAV +0.8% today (paper). — @AgenticStonker',
        xPostId: '0',
        postedAt: NOW - 35 * 60 * 1000,
      },
      {
        id: 'mock-x-001',
        refType: 'trade',
        refId: 'mock-t-003',
        content:
          'MOCK POST: Sold MCKA on momentum fade. Details on the dashboard. — @AgenticStonker',
        xPostId: '0',
        postedAt: daysAgo(1),
      },
    ],
  }
}

export function mockHoldings(): import('./types').HoldingsResponse {
  const rows = [
    { symbol: 'NVDA', address: '0xd0601ce157db5bdc3162bbac2a2c8af5320d9eec' as `0x${string}`, balance: '0.010344', raw: '10344000000000000', decimals: 18, priceUsd: 206.5, valueUsd: 2.14 },
    { symbol: 'AAPL', address: '0xaf3d76f1834a1d425780943c99ea8a608f8a93f9' as `0x${string}`, balance: '0.006441', raw: '6441000000000000', decimals: 18, priceUsd: 336.9, valueUsd: 2.17 },
    { symbol: 'AMZN', address: '0x12f190a9f9d7d37a250758b26824b97ce941bf54' as `0x${string}`, balance: '0.008532', raw: '8532000000000000', decimals: 18, priceUsd: 242.6, valueUsd: 2.07 },
    { symbol: 'MSFT', address: '0xe93237c50d904957cf27e7b1133b510c669c2e74' as `0x${string}`, balance: '0.005125', raw: '5125000000000000', decimals: 18, priceUsd: 397.7, valueUsd: 2.04 },
    { symbol: 'USDG', address: '0x5fc5360d0400a0fd4f2af552add042d716f1d168' as `0x${string}`, balance: '1.619733', raw: '1619733', decimals: 6, priceUsd: 1, valueUsd: 1.62 },
  ]
  return {
    tokenId: 1,
    nftContract: '0x0f73a2d18670f33808c2bec828dd4b58f636c1e9' as `0x${string}`,
    wallet: '0x5264c66b794c4aa356970f55af2052a82ba86044' as `0x${string}`,
    holder: '0x6c097368f2f04dc1586a42fffc36ec54bde00fcf' as `0x${string}`,
    holdings: rows,
    navUsd: 10.04,
    updatedAt: Date.now(),
  }
}

export function mockRealBroker(): RealBrokerResponse {
  // Contract/token addresses are the real public mainnet constants; the
  // wallet/holder/balances below are SYNTHETIC mock values.
  return {
    tokenId: 1,
    nftContract: '0x539cdd042c2f3d93ebc5be7dfff0c79f3b4fabf0',
    wallet: mockAddr(20),
    holder: mockAddr(21),
    holdings: [
      {
        symbol: 'AAPL',
        address: '0xaf3d76f1834a1d425780943c99ea8a608f8a93f9',
        balance: '2.5',
        raw: '2500000000000000000',
        decimals: 18,
      },
      {
        symbol: 'AMZN',
        address: '0x12f190a9f9d7d37a250758b26824b97ce941bf54',
        balance: '0',
        raw: '0',
        decimals: 18,
      },
      {
        symbol: 'NVDA',
        address: '0xd0601ce157db5bdc3162bbac2a2c8af5320d9eec',
        balance: '1.75',
        raw: '1750000000000000000',
        decimals: 18,
      },
      {
        symbol: 'USDG',
        address: '0x5fc5360d0400a0fd4f2af552add042d716f1d168',
        balance: '412.19',
        raw: '412190000',
        decimals: 6,
      },
    ],
    eth: { balance: '0.05', raw: '50000000000000000', decimals: 18 },
    compat: {
      ownerCallOk: true,
      strangerCallReverts: true,
      checkedAt: NOW - 6 * 60 * 1000,
    },
  }
}

export function mockMethodology(): Record<string, unknown> {
  return {
    name: 'MOCK: Agentic Stonker Momentum Index',
    version: 1,
    note: 'MOCK fixture — the real document is served by the gateway from engine defaults.',
  }
}

// ---------------------------------------------------------------------------
// Admin fixtures
// ---------------------------------------------------------------------------

export function mockConfigList(): ConfigListResponse {
  return {
    items: [
      {
        key: 'approval_mode',
        value: 'manual',
        version: 1,
        updatedAt: daysAgo(10),
        updatedBy: 'admin:mock@example.com',
      },
      {
        key: 'engine',
        value: { W_MAX: 0.2, CASH_RESERVE: 0.02, K_MAX: 15 },
        version: 3,
        updatedAt: daysAgo(2),
        updatedBy: 'admin:mock@example.com',
      },
      {
        key: 'kill_switch',
        value: false,
        version: 2,
        updatedAt: daysAgo(5),
        updatedBy: 'admin:mock@example.com',
      },
      {
        key: 'mode',
        value: 'paper',
        version: 1,
        updatedAt: daysAgo(14),
        updatedBy: 'admin:mock@example.com',
      },
      {
        key: 'paused',
        value: false,
        version: 4,
        updatedAt: daysAgo(1),
        updatedBy: 'admin:mock@example.com',
      },
    ],
  }
}

export function mockAudit(): AuditResponse {
  return {
    items: [
      {
        id: 'mock-a-005',
        ts: NOW - 38 * 60 * 1000,
        actor: 'system:workflow',
        action: 'trade.confirmed',
        refType: 'trade',
        refId: 'mock-t-004',
        detail: { fillPriceUsd: 141.02 },
      },
      {
        id: 'mock-a-004',
        ts: NOW - 41 * 60 * 1000,
        actor: 'admin:mock@example.com',
        action: 'rebalance.trigger',
        refType: null,
        refId: null,
        detail: { status: 200 },
      },
      {
        id: 'mock-a-003',
        ts: daysAgo(1),
        actor: 'admin:mock@example.com',
        action: 'rebalance.approve',
        refType: 'workflow',
        refId: 'mock-wf-20260721-a',
        detail: null,
      },
      {
        id: 'mock-a-002',
        ts: daysAgo(2),
        actor: 'system:cron-daily',
        action: 'drift.breach',
        refType: 'portfolio_state',
        refId: 'mock-state-013',
        detail: { targetStateId: 'mock-state-014' },
      },
      {
        id: 'mock-a-001',
        ts: daysAgo(5),
        actor: 'admin:mock@example.com',
        action: 'config.update',
        refType: 'config',
        refId: 'kill_switch',
        detail: { version: 2, value: false },
      },
    ],
  }
}

export function mockHealthFull(): AdminHealthResponse {
  return {
    ok: true,
    db: true,
    components: [
      {
        component: 'daily',
        status: 'ok',
        detail: { candlesRefreshed: 6 },
        updatedAt: NOW - 55 * 60 * 1000,
      },
      {
        component: 'scan',
        status: 'warn',
        detail: { priced: 5, universe: 6, errors: 1 },
        updatedAt: NOW - 4 * 60 * 1000,
      },
      {
        component: 'weekly',
        status: 'ok',
        detail: null,
        updatedAt: daysAgo(1),
      },
      {
        component: 'x-posts',
        status: 'ok',
        detail: { queued: 0 },
        updatedAt: NOW - 30 * 60 * 1000,
      },
    ],
    ts: NOW,
    auth: { actor: 'admin:mock@example.com' },
  }
}

let mockVersion = 10

export function mockPutConfig(key: unknown, _value: unknown): ConfigPutResponse {
  return { ok: true, key: String(key), version: ++mockVersion }
}

export function mockRebalance(): RebalanceResponse {
  return {
    ok: true,
    id: `mock-wf-${Date.now()}`,
    status: { status: 'running' },
  }
}

export function mockApprove(
  instanceId: unknown,
  approve: unknown,
): ApproveResponse {
  return { ok: true, instanceId: String(instanceId), approve: approve === true }
}

export function mockPause(): PauseResumeResponse {
  return { ok: true, paused: true, version: ++mockVersion }
}

export function mockResume(): PauseResumeResponse {
  return { ok: true, paused: false, version: ++mockVersion }
}

export function mockKillSwitch(active: unknown): KillSwitchResponse {
  return { ok: true, killSwitch: active !== false, version: ++mockVersion }
}

// ---------------------------------------------------------------------------
// Route tables used by the mock client
// ---------------------------------------------------------------------------

/** GET fixtures keyed by bare path (query strings stripped by the client). */
export const MOCK_GET_ROUTES: Record<string, () => unknown> = {
  '/api/v1/health': mockHealth,
  '/api/v1/portfolio/current': mockPortfolioCurrent,
  '/api/v1/portfolio/history': mockPortfolioHistory,
  '/api/v1/performance': mockPerformance,
  '/api/v1/trades': mockTrades,
  '/api/v1/universe/latest': mockUniverse,
  '/api/v1/signals/latest': mockSignals,
  '/api/v1/posts': mockPosts,
  '/api/v1/methodology': mockMethodology,
  '/api/v1/real-broker': mockRealBroker,
  '/api/v1/holdings': mockHoldings,
  '/admin/api/config': mockConfigList,
  '/admin/api/audit': mockAudit,
  '/admin/api/health/full': mockHealthFull,
}

/** Parameterized fixtures (mutations + /trades/:id). */
export const MOCK_ADMIN_ROUTES: Record<string, (...args: unknown[]) => unknown> = {
  trade: (id) => mockTradeDetail(id),
  putConfig: (key, value) => mockPutConfig(key, value),
  rebalance: () => mockRebalance(),
  approve: (instanceId, approve) => mockApprove(instanceId, approve),
  pause: () => mockPause(),
  resume: () => mockResume(),
  killSwitch: (active) => mockKillSwitch(active),
}
